import { motion } from "framer-motion";
import {
    Lock,
    KeyRound,
    Activity,
    ShieldCheck,
    Globe2,
    Server,
    FileCheck2,
    AlertTriangle,
    Mail,
    ArrowUpRight,
    Sparkles,
} from "lucide-react";

const PILLARS = [
    {
        icon: Lock,
        accent: "text-electric",
        chip: "bg-electric/10 border-electric/25",
        ring: "from-electric/30 to-electric/0",
        title: "Encryption",
        sub: "Always, by default — never opt-in.",
        rows: [
            "AES-256-GCM at rest",
            "TLS 1.2 in transit",
            "Per-tenant data keys via envelope encryption",
            "Optional customer-managed keys (BYOK) on Enterprise",
        ],
    },
    {
        icon: KeyRound,
        accent: "text-cyan-300",
        chip: "bg-cyan-300/10 border-cyan-300/25",
        ring: "from-cyan-400/30 to-cyan-400/0",
        title: "Identity & access",
        sub: "Least privilege, every step of the way.",
        rows: [
            "SSO Support",
            "RBAC Support",
            "Audit Logs",
            "Access Audits",
        ],
    },
    {
        icon: Activity,
        accent: "text-emerald-300",
        chip: "bg-emerald-300/10 border-emerald-300/25",
        ring: "from-emerald-400/30 to-emerald-400/0",
        title: "Operational security",
        sub: "Engineered like infrastructure, not policy PDFs.",
        rows: [
            "Penetration Testing",
            "Immutable infrastructure-as-code",
            "Signed, reproducible builds",
            "Dependency Scanning",
        ],
    },
    {
        icon: Server,
        accent: "text-amber-glow",
        chip: "bg-amber-300/10 border-amber-300/25",
        ring: "from-amber-400/30 to-amber-400/0",
        title: "Resilience",
        sub: "Designed to fail well.",
        rows: [
            "Multi-AZ deployments by default",
            "Point-in-time recovery",
            "Disaster Recovery",
            "Data Encryption",
        ],
    },
    {
        icon: Globe2,
        accent: "text-violet-300",
        chip: "bg-violet-300/10 border-violet-300/25",
        ring: "from-violet-400/30 to-violet-400/0",
        title: "Data residency",
        sub: "Where your data lives, you decide.",
        rows: [
            "Pin tenancy to EU, US, India or APAC",
            "Cross-region transfer",

        ],
    },
    {
        icon: FileCheck2,
        accent: "text-rose-300",
        chip: "bg-rose-300/10 border-rose-300/25",
        ring: "from-rose-400/30 to-rose-400/0",
        title: "Audit & monitoring",
        sub: "Every change leaves a trail.",
        rows: [
            "Immutable audit log",
            "Anomaly detection",
            "Audit log retention",
            "24/7 monitoring",
        ],
    },
];

const CERTS = [
    { name: "SOC 2 Type II", sub: "Continuous · annual report" },
    { name: "ISO/IEC 27001:2022", sub: "Certified · global scope" },
    { name: "GDPR", sub: "Processor + EU SCCs" },
    { name: "HIPAA", sub: "BAA on request" },
    { name: "ISO 27701", sub: "Privacy management" },
    { name: "CSA STAR Level 1", sub: "Cloud transparency" },
];

const STATS = [
    { v: "99.997%", l: "uptime · 90d", accent: "text-emerald-300" },
    { v: "24h", l: "breach notification", accent: "text-electric" },
    { v: "0", l: "zero-days exploited", accent: "text-amber-glow" },
    { v: "5 min", l: "RPO target", accent: "text-violet-300" },
];

export default function SecurityPage() {
    return (
        <div className="pt-28 lg:pt-32" data-testid="security-page">
            {/* Hero */}
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1 mb-6">
                            <ShieldCheck className="w-3.5 h-3.5 text-electric" />
                            <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-zinc-300">
                                Security at ApexBrain
                            </span>
                        </div>
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // engineered for trust
                        </p>
                        <h1
                            className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.04]"
                            data-testid="security-title"
                        >
                            Boring, where it should be.
                            <br />
                            <span className="gradient-text">
                                Paranoid, where it matters.
                            </span>
                        </h1>
                    </div>
                    <p className="lg:col-span-5 text-base sm:text-lg text-zinc-400 leading-relaxed font-light">
                        ApexBrain is the operating layer for a whole company.
                        Security is treated as a product surface — encoded into
                        controls, not bolted on as policy PDFs.
                    </p>
                </div>

                {/* Stat row */}
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="security-stats">
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.l}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4"
                        >
                            <div
                                className={`font-outfit text-xl sm:text-2xl font-semibold tabular-nums ${s.accent}`}
                            >
                                {s.v}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-code mt-1">
                                {s.l}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Pillars */}
            <section className="relative py-12" data-testid="security-pillars">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                    {PILLARS.map((p, i) => (
                        <motion.div
                            key={p.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-50px" }}
                            transition={{ duration: 0.4, delay: i * 0.05 }}
                            whileHover={{ y: -3 }}
                            className="relative rounded-2xl border border-white/[0.06] bg-obsidian-200/70 backdrop-blur-sm p-6 overflow-hidden"
                            data-testid={`security-pillar-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        >
                            <div
                                className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br ${p.ring} blur-3xl pointer-events-none`}
                            />
                            <span
                                className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border ${p.chip}`}
                            >
                                <p.icon className={`w-4 h-4 ${p.accent}`} />
                            </span>
                            <h3 className="relative mt-5 font-outfit text-lg font-semibold text-white">
                                {p.title}
                            </h3>
                            <p
                                className={`relative mt-1 text-xs ${p.accent} font-code uppercase tracking-[0.15em]`}
                            >
                                {p.sub}
                            </p>
                            <ul className="relative mt-5 space-y-2 pt-5 border-t border-white/[0.06]">
                                {p.rows.map((r) => (
                                    <li
                                        key={r}
                                        className="flex items-start gap-2 text-sm text-zinc-300"
                                    >
                                        <span
                                            className={`shrink-0 mt-2 w-1.5 h-1.5 rounded-full ${p.accent.replace("text-", "bg-")}`}
                                        />
                                        <span>{r}</span>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Certifications */}
            {/* <section className="relative py-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-amber-glow font-semibold mb-3">
                        // certifications · audits
                    </p>
                    <h2 className="font-outfit text-2xl sm:text-3xl font-bold text-white leading-snug">
                        Independently verified.
                        <br />
                        <span className="text-zinc-500">Reports under NDA.</span>
                    </h2>

                    <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="security-certs">
                        {CERTS.map((c) => (
                            <div
                                key={c.name}
                                className="rounded-xl border border-white/[0.08] bg-obsidian-200/70 p-4 text-center"
                            >
                                <div className="font-outfit text-sm font-semibold text-white">
                                    {c.name}
                                </div>
                                <div className="font-code text-[10px] text-zinc-500 mt-1">
                                    {c.sub}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section> */}

            {/* Responsible disclosure */}
            <section className="relative py-12 pb-28" data-testid="security-disclosure">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-obsidian-100 via-obsidian-200 to-obsidian-300 p-10 sm:p-14 overflow-hidden">
                        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[70%] h-40 bg-electric/25 blur-[120px] rounded-full pointer-events-none" />
                        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
                            <div className="lg:col-span-7">
                                <AlertTriangle className="w-5 h-5 text-amber-glow mb-3" />
                                <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-glow font-semibold">
                                    // responsible disclosure
                                </p>
                                <h2 className="font-outfit text-3xl sm:text-4xl font-semibold tracking-tight text-white mt-3 leading-[1.1]">
                                    Found something we should know?
                                </h2>
                                <p className="mt-4 max-w-2xl text-zinc-400 leading-relaxed font-light">
                                    Report security issues to{" "}
                                    <a
                                        className="text-white hover:underline"
                                        href="mailto:security@apexbrain.in"
                                    >
                                        security@apexbrain.in
                                    </a>{" "}
                                    We respond within 24 hours, fix critical
                                    issues within 72.
                                </p>
                            </div>
                            <div className="lg:col-span-5 flex flex-wrap gap-3">
                                <a
                                    href="mailto:security@apexbrain.in"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-5 py-3 rounded-full hover:brightness-110 transition"
                                    data-testid="security-email-cta"
                                >
                                    <Mail className="w-4 h-4" />
                                    security@apexbrain.in
                                </a>
                                {/* <a
                                    href="https://hackerone.com/apexbrain"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-5 py-3 rounded-full hover:bg-white/10 transition"
                                >
                                    Bug bounty
                                    <ArrowUpRight className="w-4 h-4 text-electric" />
                                </a> */}
                                {/* <a
                                    href="/compliance"
                                    className="inline-flex items-center gap-2 text-sm font-semibold text-white glass px-5 py-3 rounded-full hover:bg-white/10 transition"
                                >
                                    <Sparkles className="w-4 h-4 text-amber-glow" />
                                    Compliance docs
                                </a> */}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
